/**
 * Context File Service
 * API client for context file (reference document) management per episode.
 */

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

export interface ContextFile {
  id: string;
  session_id: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  s3_key: string;
  description: string | null;
  description_status: 'pending' | 'generating' | 'completed' | 'failed';
  hidden_from_share: number;
  uploader_email: string;
  created_at: string;
  updated_at: string;
  downloadUrl?: string;
}

async function fetchWithAuth(url: string, token?: string, options: RequestInit = {}): Promise<Response> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {})
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || body.message || `Request failed: ${res.status}`);
  }
  return res;
}

export async function list(sessionId: string, token?: string): Promise<ContextFile[]> {
  const res = await fetchWithAuth(`${API_BASE_URL}/sessions/${sessionId}/context-files`, token);
  const data = await res.json();
  return data.files;
}

export async function upload(sessionId: string, files: File[], token?: string): Promise<ContextFile[]> {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  const res = await fetchWithAuth(
    `${API_BASE_URL}/sessions/${sessionId}/context-files`,
    token,
    { method: 'POST', body: formData }
  );
  const data = await res.json();
  return data.files;
}

export async function getWithUrl(sessionId: string, fileId: string, token?: string): Promise<ContextFile> {
  const res = await fetchWithAuth(`${API_BASE_URL}/sessions/${sessionId}/context-files/${fileId}`, token);
  return res.json();
}

export async function getContent(sessionId: string, fileId: string, token?: string): Promise<string> {
  const res = await fetchWithAuth(`${API_BASE_URL}/sessions/${sessionId}/context-files/${fileId}/content`, token);
  return res.text();
}

export async function updateMetadata(
  sessionId: string, fileId: string,
  updates: { description?: string; hiddenFromShare?: boolean },
  token?: string
): Promise<void> {
  await fetchWithAuth(
    `${API_BASE_URL}/sessions/${sessionId}/context-files/${fileId}`,
    token,
    { method: 'PUT', body: JSON.stringify(updates) }
  );
}

export async function updateContent(
  sessionId: string, fileId: string, content: string, token?: string
): Promise<void> {
  await fetchWithAuth(
    `${API_BASE_URL}/sessions/${sessionId}/context-files/${fileId}/content`,
    token,
    { method: 'PUT', body: JSON.stringify({ content }) }
  );
}

export async function deleteFile(sessionId: string, fileId: string, token?: string): Promise<void> {
  await fetchWithAuth(
    `${API_BASE_URL}/sessions/${sessionId}/context-files/${fileId}`,
    token,
    { method: 'DELETE' }
  );
}

export async function listPublic(shareToken: string): Promise<ContextFile[]> {
  const res = await fetch(`${API_BASE_URL}/public/share/${shareToken}/context-files`);
  if (!res.ok) throw new Error('Failed to list public context files');
  const data = await res.json();
  return data.files;
}

export async function getPublicDownloadUrl(shareToken: string, fileId: string): Promise<string> {
  const res = await fetch(`${API_BASE_URL}/public/share/${shareToken}/context-files/${fileId}/download`);
  if (!res.ok) throw new Error('Failed to get public download URL');
  const data = await res.json();
  return data.downloadUrl;
}
